'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Calendar,
  Clock,
  ArrowLeft,
  Check,
  Copy,
  MessageCircle,
  BookOpen,
  ShieldCheck,
  Tag,
  ChevronRight,
  ArrowRight,
} from 'lucide-react';
import EditorialMarkdown, { slugifyHeading } from '@/components/EditorialMarkdown';
import Breadcrumbs from '@/components/Breadcrumbs';
import type { BlogPost } from '@/lib/editorial';

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(value));
  } catch {
    return value;
  }
}

function getReadTime(content: string) {
  if (!content) return '3 min read';
  const words = content.replace(/[#*`_~]/g, '').trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

function getAuthorInitials(name: string) {
  if (!name) return 'AJ';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

type BlogPostClientProps = {
  post: BlogPost;
  relatedPosts: BlogPost[];
  siteUrl: string;
};

export default function BlogPostClient({ post, relatedPosts, siteUrl }: BlogPostClientProps) {
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const postUrl = `${siteUrl}/blog/${post.slug}`;
  const readTime = getReadTime(post.content_markdown);

  // Extract Table of Contents from markdown ## headings
  const tocItems = (post.content_markdown || '')
    .split('\n')
    .filter((line) => line.trim().startsWith('## '))
    .map((line) => {
      const text = line.trim().slice(3);
      return {
        text,
        id: slugifyHeading(text),
      };
    });

  // Highlight active TOC item on scroll
  useEffect(() => {
    if (!tocItems.length) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (let i = tocItems.length - 1; i >= 0; i--) {
        const item = tocItems[i];
        const el = document.getElementById(item.id);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(item.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const shareText = encodeURIComponent(`Read "${post.title}" on AstroJi`);
  const whatsappUrl = `https://api.whatsapp.com/send?text=${shareText}%20${encodeURIComponent(postUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(postUrl)}`;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        items={[
          { label: 'Blog', href: '/blog' },
          { label: post.title }
        ]}
        className="py-2 mb-2"
      />

      {/* Main Article Header */}
      <header className="py-6 sm:py-8 lg:py-10 max-w-4xl border-b border-orange-100/80 mb-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Link
            href="/blog"
            className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50/60 px-3.5 py-1 text-xs font-semibold text-accent hover:bg-orange-100 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5 text-primary" />
            <span>Back to guides</span>
          </Link>
          {post.related_product_tags?.[0] && (
            <span className="rounded-full bg-primary/15 text-primary-dark px-3 py-1 text-xs font-semibold border border-primary/20">
              {post.related_product_tags[0]}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
            <Clock className="h-3.5 w-3.5 text-primary" />
            {readTime}
          </span>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-accent leading-tight">
          {post.title}
        </h1>

        <p className="mt-4 text-base sm:text-xl leading-relaxed text-text-secondary">
          {post.excerpt}
        </p>

        {/* Author & Publish Bar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-orange-100/60">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-tr from-accent to-primary text-sm font-bold text-white shadow-xs">
              {getAuthorInitials(post.author_name)}
            </div>
            <div>
              <p className="text-sm font-semibold text-accent leading-tight">{post.author_name}</p>
              <p className="text-xs text-text-secondary">
                {post.author_role || 'Spiritual & Gemstone Specialist'} • Published {formatDate(post.published_at)}
              </p>
            </div>
          </div>

          {/* Inline Quick Share */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-accent shadow-xs hover:bg-orange-50 transition-colors cursor-pointer"
              title="Copy guide URL"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5 text-primary" />}
              <span>{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors shadow-xs"
              title="Share on WhatsApp"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      {/* 12-Column Main Section (Desktop Grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12 items-start mb-16">
        {/* Main Article Content (8 cols on Laptop) */}
        <article className="lg:col-span-8">
          {/* Featured Hero Image */}
          {post.hero_image_url && (
            <div className="mb-8 overflow-hidden rounded-2xl border border-orange-100 bg-orange-50/30 shadow-md">
              <img
                src={post.hero_image_url}
                alt={post.hero_image_alt || `${post.title} - AstroJi Spiritual Article`}
                className="h-auto w-full max-h-[480px] object-cover"
              />
            </div>
          )}

          {/* Article Markdown Body */}
          <EditorialMarkdown content={post.content_markdown} className="prose-container" />

          {/* Social Share Footer Bar */}
          <div className="mt-12 rounded-2xl border border-orange-200/80 bg-gradient-to-r from-orange-50 via-amber-50/50 to-orange-50 p-6 shadow-xs">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-serif text-lg font-bold text-accent">Found this guide helpful?</h4>
                <p className="text-xs sm:text-sm text-text-secondary mt-0.5">Share it with friends and family embarking on their spiritual journey.</p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-xs font-semibold text-accent shadow-xs hover:bg-orange-100 transition-colors cursor-pointer"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-primary" />}
                  <span>{copied ? 'Copied Link' : 'Copy Link'}</span>
                </button>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-green-700 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>WhatsApp</span>
                </a>

                <a
                  href={twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-accent text-white shadow-xs hover:bg-primary transition-colors"
                  title="Share on X (Twitter)"
                >
                  <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Author Credibility Box */}
          <div className="mt-8 rounded-2xl border border-orange-100 bg-white p-6 shadow-xs flex flex-col sm:flex-row gap-5 items-start sm:items-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-tr from-accent to-primary text-base font-bold text-white shrink-0 shadow-md">
              {getAuthorInitials(post.author_name)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-serif text-lg font-bold text-accent">{post.author_name}</h4>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-accent">
                  Verified Specialist
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-1">
                {post.author_role || 'AstroJi Senior Editorial Specialist in Sacred Gemstones & Vedic Rituals.'}
              </p>
              <p className="text-xs text-text-secondary/80 mt-2 leading-relaxed">
                Dedicated to dispelling myths and delivering lab-certified clarity on spiritual products for seekers nationwide.
              </p>
            </div>
          </div>

          {/* Related Tags */}
          {post.related_product_tags.length > 0 && (
            <aside className="mt-8 rounded-2xl border border-orange-100 bg-white p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-accent flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" />
                <span>Explore Related Products & Topics</span>
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {post.related_product_tags.map((tag) => (
                  <Link
                    key={tag}
                    href={`/products?byTag=${encodeURIComponent(tag)}`}
                    className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50/50 px-3.5 py-1.5 text-xs font-semibold text-accent hover:border-primary hover:bg-orange-100 transition-colors"
                  >
                    <span>{tag}</span>
                    <ArrowRight className="h-3 w-3 text-primary" />
                  </Link>
                ))}
              </div>
            </aside>
          )}
        </article>

        {/* Sticky Desktop Sidebar (4 cols on Laptop) */}
        <aside className="lg:col-span-4 hidden lg:block sticky top-24 space-y-6">
          {/* Table of Contents Card */}
          {tocItems.length > 0 && (
            <div className="rounded-2xl border border-orange-100 bg-white p-6 shadow-xs">
              <h3 className="font-serif text-base font-bold text-accent flex items-center gap-2 pb-3 border-b border-orange-100">
                <BookOpen className="h-4 w-4 text-primary" />
                <span>In this guide</span>
              </h3>
              <ul className="mt-4 space-y-2.5 text-xs sm:text-sm">
                {tocItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className={`block leading-snug transition-colors py-1 pl-2.5 border-l-2 ${
                          isActive
                            ? 'border-primary font-semibold text-primary bg-orange-50/60 rounded-r-md'
                            : 'border-transparent text-text-secondary hover:text-accent hover:border-orange-200'
                        }`}
                      >
                        {item.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* AstroJi Store Promo Card */}
          <div className="rounded-2xl border border-orange-200/80 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-white p-6 shadow-sm relative overflow-hidden">
            <ShieldCheck className="h-8 w-8 text-primary mb-3" />
            <h3 className="font-serif text-lg font-bold text-accent">Lab-Certified Rudraksha & Gemstones</h3>
            <p className="mt-2 text-xs leading-relaxed text-text-secondary">
              Every item at AstroJi is tested by independent gemstone laboratories. Free shipping & COD available.
            </p>
            <div className="mt-5 space-y-2.5">
              <Link
                href="/products"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-xs font-semibold text-white shadow-xs hover:bg-primary transition-all active:scale-95"
              >
                <span>Browse Products</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                href="/consultations"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-300 bg-white px-4 py-2.5 text-xs font-semibold text-accent hover:bg-orange-50 transition-all active:scale-95"
              >
                <span>Consult an Astrologer</span>
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Related Guides Section at Bottom */}
      {relatedPosts.length > 0 && (
        <section aria-labelledby="related-guides-heading" className="py-12 border-t border-orange-100">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Continue Reading</p>
              <h2 id="related-guides-heading" className="font-serif text-2xl sm:text-3xl font-bold text-accent mt-1">
                More Spiritual Guides
              </h2>
            </div>
            <Link
              href="/blog"
              className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-primary hover:underline"
            >
              <span>View all guides</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedPosts.slice(0, 3).map((rel) => (
              <article
                key={rel.id}
                className="group flex flex-col justify-between rounded-2xl border border-orange-100/80 bg-white p-5 shadow-xs transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-md"
              >
                <div>
                  <Link
                    href={`/blog/${rel.slug}`}
                    className="block relative aspect-[16/10] overflow-hidden rounded-xl bg-orange-50 mb-4 border border-orange-100/50"
                  >
                    {rel.hero_image_url ? (
                      <img
                        src={rel.hero_image_url}
                        alt={rel.hero_image_alt || rel.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                        <BookOpen className="h-8 w-8 text-primary/40 mb-1" />
                        <span className="text-[11px] font-medium text-text-secondary">Astroji Guide</span>
                      </div>
                    )}
                  </Link>

                  <div className="flex items-center gap-2 text-xs text-text-secondary font-medium mb-2">
                    <Calendar className="h-3 w-3 text-primary" />
                    <span>{formatDate(rel.published_at)}</span>
                  </div>

                  <h3 className="font-serif text-base font-bold leading-snug text-accent group-hover:text-primary transition-colors line-clamp-2">
                    <Link href={`/blog/${rel.slug}`}>{rel.title}</Link>
                  </h3>
                </div>

                <Link
                  href={`/blog/${rel.slug}`}
                  className="mt-4 pt-3 border-t border-orange-100/80 inline-flex items-center justify-between text-xs font-semibold text-primary"
                >
                  <span>Read guide</span>
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
